import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Check, Loader2, ArrowRight, PartyPopper } from "lucide-react";
import { apiClient } from "../lib/api";
import { VEHICLES, ADD_ONS, OCCASIONS } from "../lib/constants";

const initial = {
  full_name: "",
  phone: "",
  email: "",
  vehicle: "escalade",
  service_type: "hourly",
  pickup_date: "",
  pickup_time: "",
  hours: 3,
  pickup_location: "",
  dropoff_location: "",
  occasion: "",
  add_ons: [],
  passengers: 2,
  notes: "",
};

const Label = ({ children, htmlFor }) => (
  <label
    htmlFor={htmlFor}
    className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-2 block font-semibold"
  >
    {children}
  </label>
);

const inputClass =
  "w-full bg-black border border-zinc-800 focus:border-[#E50914] focus:outline-none focus:ring-0 rounded-none text-white px-4 py-3.5 text-sm placeholder:text-zinc-700 transition-colors";

const selectClass = inputClass + " appearance-none cursor-pointer";

const Field = ({ children }) => <div className="flex flex-col">{children}</div>;

export const BookingForm = ({ initialVehicle }) => {
  const [data, setData] = useState({ ...initial, vehicle: initialVehicle || "escalade" });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);

  // Sync external vehicle change (when user clicks a fleet card)
  useEffect(() => {
    if (!initialVehicle) return;
    setData((d) => ({
      ...d,
      vehicle: initialVehicle,
      service_type:
        initialVehicle === "rolls_royce"
          ? "quote_request"
          : d.service_type === "quote_request"
          ? "hourly"
          : d.service_type,
    }));
  }, [initialVehicle]);

  const update = (k, v) => setData((d) => ({ ...d, [k]: v }));
  const toggleAddon = (id) =>
    setData((d) => ({
      ...d,
      add_ons: d.add_ons.includes(id) ? d.add_ons.filter((x) => x !== id) : [...d.add_ons, id],
    }));

  const isQuote = data.vehicle === "rolls_royce";
  const vehicle = VEHICLES.find((v) => v.id === data.vehicle);

  // Live estimate
  const estimate = (() => {
    if (isQuote) return null;
    if (data.service_type === "hourly" && vehicle && data.hours) {
      const tier = vehicle.tiers.find(
        (t) => data.hours >= t.minHours && data.hours <= t.maxHours
      );
      if (tier) return tier.rate * data.hours;
    }
    if (data.service_type === "wedding_package" && vehicle?.wedding) {
      const extra = Math.max(0, (data.hours || 5) - 5) * (vehicle.additionalHour || 0);
      return vehicle.wedding + extra;
    }
    if (data.service_type === "prom_package" && vehicle?.prom) {
      const extra = Math.max(0, (data.hours || 5) - 5) * (vehicle.additionalHour || 0);
      return vehicle.prom + extra;
    }
    return null;
  })();

  const submit = async (e) => {
    e.preventDefault();
    if (!data.full_name || !data.phone || !data.email || !data.pickup_date || !data.pickup_time || !data.pickup_location) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...data,
        hours: isQuote ? null : data.hours,
        service_type: isQuote ? "quote_request" : data.service_type,
      };
      const res = await apiClient.post("/bookings", payload);
      setSuccess(res.data);
      toast.success("Request received. We'll be in touch shortly.");
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || "Something went wrong. Please call us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div
        data-testid="booking-success"
        className="bg-[#0A0A0A] border border-zinc-800 p-10 md:p-16 text-center max-w-2xl mx-auto"
      >
        <div className="w-16 h-16 bg-[#E50914]/10 border border-[#E50914] flex items-center justify-center mx-auto mb-6">
          <PartyPopper className="w-8 h-8 text-[#E50914]" />
        </div>
        <div className="text-[10px] tracking-[0.3em] text-[#E50914] uppercase mb-4">
          Request Received
        </div>
        <h3 className="font-display text-3xl md:text-4xl uppercase tracking-tight">
          Thank you, {success.full_name.split(" ")[0]}.
        </h3>
        <p className="mt-5 text-zinc-400 leading-relaxed">
          We've received your request for the{" "}
          <span className="text-white">
            {VEHICLES.find((v) => v.id === success.vehicle)?.name}
          </span>{" "}
          on <span className="text-white">{success.pickup_date}</span> at{" "}
          <span className="text-white">{success.pickup_time}</span>. A team member will contact you
          shortly to confirm details
          {success.estimated_total ? (
            <>
              . Estimated total: <span className="text-[#E50914] font-mono-data">
                ${success.estimated_total.toFixed(2)}
              </span>
            </>
          ) : (
            "."
          )}
        </p>
        <div className="mt-8 text-xs text-zinc-500 font-mono-data tracking-wide">
          REF: {success.id.slice(0, 8).toUpperCase()}
        </div>
        <button
          onClick={() => {
            setSuccess(null);
            setData({ ...initial });
          }}
          data-testid="booking-new-btn"
          className="mt-8 text-xs uppercase tracking-[0.25em] text-zinc-400 hover:text-white border-b border-zinc-700 hover:border-white pb-1 transition-all"
        >
          Submit Another Request
        </button>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      data-testid="booking-form"
      className="bg-[#0A0A0A] border border-zinc-800 p-7 md:p-10 max-w-5xl mx-auto"
    >
      <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="text-[10px] tracking-[0.3em] text-[#E50914] uppercase mb-2">
            ⏤ Step 1 of 1
          </div>
          <h3 className="font-display text-3xl uppercase tracking-tight">
            {isQuote ? "Request a Quote" : "Reserve Your Ride"}
          </h3>
        </div>
        {estimate !== null && !isQuote && (
          <div className="border border-[#E50914] px-5 py-3 text-right">
            <div className="text-[9px] tracking-[0.3em] text-zinc-500 uppercase">Live Estimate</div>
            <div className="font-mono-data text-2xl text-[#E50914] font-bold">
              ${estimate.toLocaleString()}
            </div>
          </div>
        )}
      </div>

      {/* Vehicle picker */}
      <div className="mb-8">
        <Label>Vehicle</Label>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {VEHICLES.map((v) => (
            <button
              key={v.id}
              type="button"
              onClick={() => {
                update("vehicle", v.id);
                if (v.id === "rolls_royce") update("service_type", "quote_request");
                else if (data.service_type === "quote_request") update("service_type", "hourly");
              }}
              data-testid={`form-vehicle-${v.id}`}
              className={`text-left p-4 border transition-all ${
                data.vehicle === v.id
                  ? "border-[#E50914] bg-[#E50914]/5"
                  : "border-zinc-800 hover:border-zinc-600"
              }`}
            >
              <div className="font-display text-sm uppercase tracking-tight">{v.name}</div>
              <div className="text-[10px] text-zinc-500 mt-1 uppercase tracking-wider">
                {v.quoteOnly ? "Custom Quote" : `From $${v.tiers[v.tiers.length - 1].rate}/hr`}
              </div>
            </button>
          ))}
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        <Field>
          <Label htmlFor="full_name">Full Name *</Label>
          <input
            id="full_name"
            value={data.full_name}
            onChange={(e) => update("full_name", e.target.value)}
            className={inputClass}
            placeholder="Jane Smith"
            data-testid="form-full-name"
            required
          />
        </Field>
        <Field>
          <Label htmlFor="phone">Phone *</Label>
          <input
            id="phone"
            value={data.phone}
            onChange={(e) => update("phone", e.target.value)}
            className={inputClass}
            placeholder="404-555-0100"
            data-testid="form-phone"
            required
          />
        </Field>
        <Field>
          <Label htmlFor="email">Email *</Label>
          <input
            id="email"
            type="email"
            value={data.email}
            onChange={(e) => update("email", e.target.value)}
            className={inputClass}
            placeholder="you@example.com"
            data-testid="form-email"
            required
          />
        </Field>
        <Field>
          <Label htmlFor="passengers">Passengers</Label>
          <input
            id="passengers"
            type="number"
            min="1"
            max="14"
            value={data.passengers}
            onChange={(e) => update("passengers", parseInt(e.target.value) || 1)}
            className={inputClass}
            data-testid="form-passengers"
          />
        </Field>

        {!isQuote && (
          <Field>
            <Label htmlFor="service_type">Service Type</Label>
            <select
              id="service_type"
              value={data.service_type}
              onChange={(e) => update("service_type", e.target.value)}
              className={selectClass}
              data-testid="form-service-type"
            >
              <option value="hourly">Hourly</option>
              <option value="wedding_package">Wedding Package (5 hrs)</option>
              <option value="prom_package">Prom Package (5 hrs)</option>
            </select>
          </Field>
        )}

        {!isQuote && (
          <Field>
            <Label htmlFor="hours">
              Hours {data.service_type !== "hourly" && "(5 included; extras billed)"}
            </Label>
            <input
              id="hours"
              type="number"
              min={data.service_type === "hourly" ? 1 : 5}
              max="24"
              value={data.hours}
              onChange={(e) => update("hours", parseInt(e.target.value) || 1)}
              className={inputClass}
              data-testid="form-hours"
            />
          </Field>
        )}

        <Field>
          <Label htmlFor="pickup_date">Pickup Date *</Label>
          <input
            id="pickup_date"
            type="date"
            value={data.pickup_date}
            onChange={(e) => update("pickup_date", e.target.value)}
            className={inputClass}
            min={new Date().toISOString().split("T")[0]}
            data-testid="form-pickup-date"
            required
          />
        </Field>
        <Field>
          <Label htmlFor="pickup_time">Pickup Time *</Label>
          <input
            id="pickup_time"
            type="time"
            value={data.pickup_time}
            onChange={(e) => update("pickup_time", e.target.value)}
            className={inputClass}
            data-testid="form-pickup-time"
            required
          />
        </Field>

        <Field>
          <Label htmlFor="pickup_location">Pickup Location *</Label>
          <input
            id="pickup_location"
            value={data.pickup_location}
            onChange={(e) => update("pickup_location", e.target.value)}
            className={inputClass}
            placeholder="Address or venue"
            data-testid="form-pickup-location"
            required
          />
        </Field>
        <Field>
          <Label htmlFor="dropoff_location">Dropoff Location</Label>
          <input
            id="dropoff_location"
            value={data.dropoff_location}
            onChange={(e) => update("dropoff_location", e.target.value)}
            className={inputClass}
            placeholder="Optional"
            data-testid="form-dropoff-location"
          />
        </Field>

        <Field>
          <Label htmlFor="occasion">Occasion</Label>
          <select
            id="occasion"
            value={data.occasion}
            onChange={(e) => update("occasion", e.target.value)}
            className={selectClass}
            data-testid="form-occasion"
          >
            <option value="">— Select —</option>
            {OCCASIONS.map((o) => (
              <option key={o} value={o}>{o}</option>
            ))}
          </select>
        </Field>

        <Field>
          <Label htmlFor="notes">Notes</Label>
          <input
            id="notes"
            value={data.notes}
            onChange={(e) => update("notes", e.target.value)}
            className={inputClass}
            placeholder="Special requests"
            data-testid="form-notes"
          />
        </Field>
      </div>

      {/* Add-ons */}
      <div className="mt-8">
        <Label>Add-Ons (Optional — Contact for Pricing)</Label>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-1">
          {ADD_ONS.map((a) => {
            const active = data.add_ons.includes(a.id);
            return (
              <button
                key={a.id}
                type="button"
                onClick={() => toggleAddon(a.id)}
                data-testid={`form-addon-${a.id}`}
                className={`text-left p-4 border transition-all flex items-start gap-3 ${
                  active
                    ? "border-[#E50914] bg-[#E50914]/5"
                    : "border-zinc-800 hover:border-zinc-600"
                }`}
              >
                <div
                  className={`w-4 h-4 mt-0.5 border flex-shrink-0 flex items-center justify-center ${
                    active ? "bg-[#E50914] border-[#E50914]" : "border-zinc-700"
                  }`}
                >
                  {active && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
                </div>
                <div>
                  <div className="text-sm font-semibold text-white">{a.label}</div>
                  <div className="text-[10px] text-zinc-500 mt-0.5 uppercase tracking-wider">
                    {a.desc}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <p className="text-xs text-zinc-500 max-w-md leading-relaxed">
          By submitting, you agree to be contacted by SFE Black Car. Fuel, tolls and gratuity may
          apply. Final pricing confirmed at booking review.
        </p>
        <button
          type="submit"
          disabled={submitting}
          data-testid="form-submit-btn"
          className="bg-[#E50914] hover:bg-[#FF2A2A] disabled:opacity-60 disabled:cursor-not-allowed text-white px-8 py-4 text-sm font-bold uppercase tracking-[0.25em] inline-flex items-center gap-3 transition-all duration-300 hover:shadow-[0_0_24px_rgba(229,9,20,0.5)]"
        >
          {submitting ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" /> Sending...
            </>
          ) : (
            <>
              Submit Request <ArrowRight className="w-4 h-4" />
            </>
          )}
        </button>
      </div>
    </form>
  );
};
